import moment from "moment";
import {compose} from "mu-lambda";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {
    Redirect,
    Route,
    Switch,
    // @ts-expect-error - TS2305 - Module '"react-router-dom"' has no exported member 'ContextRouter'. | TS2724 - '"react-router-dom"' has no exported member named 'Match'. Did you mean 'match'?
    type ContextRouter,
    // @ts-expect-error - TS2305 - Module '"react-router-dom"' has no exported member 'ContextRouter'. | TS2724 - '"react-router-dom"' has no exported member named 'Match'. Did you mean 'match'?
    type Match,
} from "react-router-dom";

import {PageLifecycle, type TrackedPageSlug} from "@khan/analytics-performance";
import {GuideTranscript, GuideTranscripts} from "@khan/khanmigo-chat-history";
import {PermissionsInfo} from "@khan/shared-core";
import {redirect, useLocation} from "@khan/urls";

import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import withLocation, {
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import type {FeedbackType} from "#discussion-package/types.ts";

import AccessDenied from "./content-pages/access-denied.tsx";
import Badges from "./content-pages/badges.tsx";
import {ClassDetails} from "./content-pages/class-details.tsx";
import Courses from "./content-pages/courses.tsx";
import Discussion from "./content-pages/discussion.tsx";
import GuideSettings from "./content-pages/guide/guide-settings.tsx";
import ProfileProvider from "./content-pages/profile-provider.ts";
import {
    getProfileRootPath,
    profilePathIsMine,
} from "./content-pages/profile-util.ts";
import Profile from "./content-pages/profile.tsx";
import Progress from "./content-pages/progress.tsx";
import Projects from "./content-pages/projects.tsx";
import Teachers from "./content-pages/teachers.tsx";
import {
    areGuideActivitiesAccessible,
    classDescriptorClassLink,
    classLink,
    getIsKmapSynced,
    getRelationship,
    getSideNavClasses,
    getSideNavLinks,
    getUserDistrictInfo,
    getWelcomeState,
    isActivityAccessible,
    isCoachListReadable,
    isGuideHistoryAccessible,
    isGuideSettingsAccessible,
    type HomepageQuery,
    type SideNavClass,
} from "./homepage-data.ts";
import RestrictedRoute from "./restricted-route.tsx";

type Props = {
    query: HomepageQuery;
    refetch: () => void;
} & WithUserProfileProps;

// This is just to make TypeScript happy
function urlPathToFeedbackType(
    pathTabName?: string | null,
): FeedbackType | null | undefined {
    switch (pathTabName) {
        case "questions":
            return "questions";
        case "comments":
            return "comments";
        case "answers":
            return "answers";
        case "replies":
            return "replies";
        case "projectquestions":
            return "projectquestions";
        case "projectanswers":
            return "projectanswers";
        default:
            return null;
    }
}

export const ContentNav = ({
    query,
    refetch,
    profile,
}: Props): React.ReactNode | null => {
    const [pageName, setPageName] = useState<
        TrackedPageSlug | null | undefined
    >(null);
    const location = useLocation();

    const reportAnalytics = useCallback(() => {
        if (query.loading) {
            return;
        }
        if (!pageName) {
            return;
        }
        const interactive = !query.loading;

        PageLifecycle.reportPageLifecycleTiming(pageName, {
            interactive,
            usable: true,
        });
    }, [query.loading, pageName]);

    useEffect(() => {
        setPageName(null);
    }, [location.pathname]);

    useEffect(() => {
        reportAnalytics();
    }, [reportAnalytics]);

    const componentCreatedTime = useRef(moment()).current;

    const renderMyCoursesRoute = () => {
        // This is a holdover from the old LIHP.  Mobile uses the /#myCourses
        // link to launch the "My courses" page and show the modal.  Catch
        // this route and redirect to the "my courses" page and send the
        // #myCourses hash along so that it does the right thing.
        if (location.hash === "#myCourses") {
            return <Redirect to="/profile/me/courses#myCourses" />;
        }
        return null;
    };

    const renderJoiningClassCodeRoute = () => {
        // This is a re-direct for another use case, similar to above
        // /?confirmJoinClassCode=YKWQKD or /?joinedClassCode=YKWQKD
        const queryParams = location.queryParams;
        if (queryParams.confirmJoinClassCode) {
            return (
                <Redirect to={`/join/${queryParams.confirmJoinClassCode}`} />
            );
        }
        if (queryParams.joinedClassCode) {
            return (
                <Redirect
                    to={`/profile/me/class/${queryParams.joinedClassCode}`}
                />
            );
        }
        return null;
    };

    const findDistrictClass = (query: HomepageQuery): SideNavClass | null => {
        const queryParams = location.queryParams;

        if (queryParams.redirectToDistrictClass) {
            const classes = getSideNavClasses(query);

            if (classes.length > 0) {
                const districtClassCode = queryParams.redirectToDistrictClass;
                // Detect if we're looking for a specific class
                if (districtClassCode !== "1") {
                    const queriedClass = classes.find(
                        (c) =>
                            c.signupCode ===
                                queryParams.redirectToDistrictClass &&
                            (c.isK4d || c.isKmap),
                    );

                    if (queriedClass) {
                        return queriedClass;
                    }
                }

                const firstClass = classes.find(
                    ({isK4d, isKmap}) => isK4d || isKmap,
                );

                if (firstClass) {
                    return firstClass;
                }
            }
        }

        return null;
    };

    const renderDefault =
        (loading: boolean, defaultRoute: string, query: HomepageQuery) =>
        ({location}: ContextRouter) => {
            const myCoursesRoute = renderMyCoursesRoute();

            if (myCoursesRoute) {
                return myCoursesRoute;
            }

            const joiningClassCodeRoute = renderJoiningClassCodeRoute();

            if (joiningClassCodeRoute) {
                return joiningClassCodeRoute;
            }

            const districtClass = findDistrictClass(query);

            if (districtClass) {
                const districtClassLink = classDescriptorClassLink({
                    profileRoot: "/profile/me/",
                    classroomDescriptor: districtClass.classroomDescriptor,
                });

                return <Redirect to={districtClassLink} />;
            }

            return loading ? (
                <RouteLoadSpinner />
            ) : (
                <Redirect to={defaultRoute + location.search} />
            );
        };

    const sufficientlyLoaded = (pageName: TrackedPageSlug) => {
        setPageName(pageName);
        reportAnalytics();
    };

    // TODO(csilvers): this is a kaid *or username*.  Rename all
    // the vars (here and downstream) appropriately.
    const getKaid = (match: Match) => {
        if (match && match.params && match.params.kaid) {
            // "me" in the url should map to the current logged in user's
            // kaid.
            if (match.params.kaid === "me") {
                if (profile) {
                    return profile.get("kaid");
                }

                // There's no profile, so we can't resolve `me` to anything.
                // Return an empty string since there's no kaid/username to
                // handle here.
                return "";
            }
            return match.params.kaid;
        }
        return "";
    };

    const profileRoot = getProfileRootPath(profile, location);
    const profileRedirect = PermissionsInfo.isLoggedIn ? "/profile/me/" : "/";
    const isKmapSynced = getIsKmapSynced(query);
    const userDistrictInfo = getUserDistrictInfo(query);
    const isMyPage = profilePathIsMine(profile, location);
    const activityIsAccessible = isActivityAccessible(query);
    const coachListIsReadable = isCoachListReadable(query);
    const guideActivitiesAccessible = areGuideActivitiesAccessible(query);
    const guideHistoryAccessible = isGuideHistoryAccessible(query);
    const guideSettingsAccessible = isGuideSettingsAccessible(query);
    const relationship = getRelationship(query);
    const navLinks = getSideNavLinks(query, profileRoot, componentCreatedTime, {
        isActivityAccessible: activityIsAccessible,
        isMyPage,
        isCoachListReadable: coachListIsReadable,
        areGuideActivitiesAccessible: guideActivitiesAccessible,
        isGuideHistoryAccessible: guideHistoryAccessible,
        isGuideSettingsAccessible: guideSettingsAccessible,
        isKhanmigoPromoAccessible: false,
    });
    const defaultRoute = PermissionsInfo.isLoggedIn
        ? navLinks.defaultRoute
        : "/";
    const curriedGetWelcomeState = getWelcomeState(query);

    return (
        <Switch>
            <RestrictedRoute
                path="/profile/:kaid/classroom/:classroomDescriptor"
                render={({match}) => {
                    const decodedClassroomDescriptor = decodeURIComponent(
                        match.params.classroomDescriptor,
                    );
                    const classData = getSideNavClasses(query).find(
                        ({classroomDescriptor}) => {
                            return (
                                decodedClassroomDescriptor ===
                                classroomDescriptor
                            );
                        },
                    );

                    if (!classData) {
                        if (!query.loading) {
                            // If the class is not found, redirect to the default route
                            return <Redirect to={defaultRoute} />;
                        }

                        return <RouteLoadSpinner />;
                    }

                    return (
                        <ClassDetails
                            classData={classData}
                            query={query}
                            refetch={refetch}
                            curriedGetWelcomeState={curriedGetWelcomeState}
                            sufficientlyLoaded={sufficientlyLoaded}
                            userDistrictInfo={userDistrictInfo}
                        />
                    );
                }}
            />
            <RestrictedRoute
                path="/profile/:kaid/teacher/:coachKaid/class/:classId"
                render={({match}) => {
                    const classData = getSideNavClasses(query).find(
                        ({coachKaid, classId}) =>
                            match.params.coachKaid === coachKaid &&
                            match.params.classId === classId,
                    );

                    if (!classData) {
                        if (!query.loading) {
                            // If the class is not found, redirect to the default route
                            return <Redirect to={defaultRoute} />;
                        }

                        return <RouteLoadSpinner />;
                    }

                    return (
                        <Redirect
                            to={classDescriptorClassLink({
                                profileRoot: "/profile/me/",
                                classroomDescriptor:
                                    classData.classroomDescriptor,
                            })}
                        />
                    );
                }}
            />
            <RestrictedRoute
                path="/profile/:kaid/assignments/teacher/:coachKaid/class/:classId"
                render={({match}) => {
                    // TODO: TUT-783 Remove deprecated LIHP routes after 10/16/24
                    return (
                        <Redirect
                            to={classLink({
                                profileRoot: "/profile/me/",
                                classId: match.params.classId,
                                coachKaid: match.params.coachKaid,
                            })}
                        />
                    );
                }}
            />
            <Redirect from="/profile/me/assignments" to="/" />
            <RestrictedRoute
                path="/profile/:kaid/learning-paths-goals/teacher/:coachKaid/class/:classId"
                render={({match}) => {
                    // TODO: TUT-783 Remove deprecated LIHP routes after 10/16/24
                    return (
                        <Redirect
                            to={classLink({
                                profileRoot: "/profile/me/",
                                classId: match.params.classId,
                                coachKaid: match.params.coachKaid,
                            })}
                        />
                    );
                }}
            />
            <RestrictedRoute
                path="/profile/:kaid/goals/teacher/:coachKaid/class/:classId"
                render={({match}) => {
                    // TODO: TUT-783 Remove deprecated LIHP routes after 10/16/24
                    return (
                        <Redirect
                            to={classLink({
                                profileRoot: "/profile/me/",
                                classId: match.params.classId,
                                coachKaid: match.params.coachKaid,
                            })}
                        />
                    );
                }}
            />
            <RestrictedRoute
                path="/profile/:kaid/courses/teacher/:coachKaid/class/:classId"
                render={({match}) => {
                    // TODO: TUT-783 Remove deprecated LIHP routes after 10/16/24
                    return (
                        <Redirect
                            to={classLink({
                                profileRoot: "/profile/me/",
                                classId: match.params.classId,
                                coachKaid: match.params.coachKaid,
                            })}
                        />
                    );
                }}
            />
            <RestrictedRoute
                path="/profile/:kaid/class/:classCode"
                render={({match}) => {
                    const classCode = match.params.classCode;
                    let maybeRedirect: React.ReactNode = null;
                    const classLink = navLinks.classSection?.links.find(
                        ({signupCode}) => signupCode === classCode,
                    )?.href;
                    if (classLink) {
                        maybeRedirect = <Redirect to={classLink} />;
                    } else if (!query.loading) {
                        maybeRedirect = <Redirect to={defaultRoute} />;
                    }

                    return (
                        <>
                            {maybeRedirect}
                            <RouteLoadSpinner />
                        </>
                    );
                }}
            />
            <Redirect
                from="/profile/:kaid/coaches"
                to="/profile/:kaid/teachers"
            />
            <Route
                path="/profile/:kaid/teachers"
                render={({match}) =>
                    query.loading ? (
                        <RouteLoadSpinner />
                    ) : (
                        <ProfileProvider kaid={getKaid(match)}>
                            {(profilePromise) => (
                                <Teachers
                                    kaid={getKaid(match)}
                                    profilePromise={profilePromise}
                                    onSufficientlyUsable={() =>
                                        sufficientlyLoaded("lihp_teachers")
                                    }
                                    isCoachListReadable={
                                        query.loading
                                            ? false
                                            : coachListIsReadable
                                    }
                                    onClassChange={(districtClassCode) => {
                                        // The entire page needs to be
                                        // refreshed so we can reload all the
                                        // queries in order to navigate to the
                                        // newly linked class
                                        redirect(
                                            `/?redirectToDistrictClass=${districtClassCode}`,
                                        );
                                    }}
                                />
                            )}
                        </ProfileProvider>
                    )
                }
            />
            <RestrictedRoute
                path="/profile/:kaid/assignments"
                render={() => <Redirect to="/" />}
            />
            <RestrictedRoute
                path="/profile/:kaid/courses"
                render={() => (
                    <Courses
                        onSufficientlyUsable={() =>
                            sufficientlyLoaded("lihp_courses")
                        }
                    />
                )}
            />
            <Route
                path="/profile/:kaid/progress"
                render={() => {
                    if (query.loading) {
                        return <RouteLoadSpinner />;
                    }

                    return activityIsAccessible ? (
                        <Progress
                            kaid={query.data?.user?.kaid}
                            isKmapSynced={isKmapSynced}
                            isMyPage={isMyPage}
                        />
                    ) : (
                        <AccessDenied />
                    );
                }}
            />
            <Route
                path="/profile/:kaid/khanmigo/history/:transcriptId"
                render={({match}) => {
                    if (query.loading) {
                        return <RouteLoadSpinner />;
                    }

                    const {transcriptId} = match.params;

                    return guideHistoryAccessible && transcriptId ? (
                        <GuideTranscript
                            transcriptId={transcriptId}
                            relationship={relationship}
                            profilePath={match.url.replace(/\/khanmigo.*$/, "")}
                        />
                    ) : (
                        <AccessDenied />
                    );
                }}
            />
            <Route
                path="/profile/:kaid/khanmigo/history"
                render={({match}) => {
                    const kaid = query.data?.user?.kaid;

                    if (query.loading) {
                        return <RouteLoadSpinner />;
                    }

                    if (!kaid || !guideHistoryAccessible) {
                        return <AccessDenied />;
                    }

                    return (
                        <GuideTranscripts
                            kaid={kaid}
                            relationship={relationship}
                            profilePath={match.url.replace(/\/khanmigo.*$/, "")}
                        />
                    );
                }}
            />
            <Route
                path="/profile/:kaid/khanmigo/settings"
                render={({match}) => {
                    const kaid = query.data?.user?.kaid;

                    if (query.loading) {
                        return <RouteLoadSpinner />;
                    }

                    if (!kaid || !guideSettingsAccessible) {
                        return <AccessDenied />;
                    }

                    return <GuideSettings kaid={kaid} />;
                }}
            />
            {/* This is a Missions deprecated route (July 2020). We will
                continue to redirect to /progress from this path. Perhaps this
                route can be removed later down the road. */}
            <Redirect
                from="/profile/:kaid/vital-statistics"
                to={`${getProfileRootPath(profile, location)}progress`}
            />
            <Route
                path="/profile/:kaid/badges"
                render={({match}) =>
                    query.loading ? (
                        <RouteLoadSpinner />
                    ) : (
                        <ProfileProvider kaid={getKaid(match)}>
                            {(profilePromise) => (
                                <Badges
                                    kaid={getKaid(match)}
                                    profilePromise={profilePromise}
                                    profilePath={match.url.replace(
                                        "/badges",
                                        "",
                                    )}
                                    isMyPage={isMyPage}
                                    onSufficientlyUsable={() =>
                                        sufficientlyLoaded(
                                            "lihp_profile_badges",
                                        )
                                    }
                                />
                            )}
                        </ProfileProvider>
                    )
                }
            />
            <Route
                path="/profile/:kaid/projects"
                render={({match}) =>
                    query.loading ? (
                        <RouteLoadSpinner />
                    ) : (
                        <ProfileProvider kaid={getKaid(match)}>
                            {(profilePromise) => (
                                <Projects
                                    kaid={getKaid(match)}
                                    profilePromise={profilePromise}
                                    profilePath={match.url.replace(
                                        /\/projects.*/,
                                        "",
                                    )}
                                    isMyPage={isMyPage}
                                    onSufficientlyUsable={() =>
                                        sufficientlyLoaded(
                                            "lihp_profile_projects",
                                        )
                                    }
                                />
                            )}
                        </ProfileProvider>
                    )
                }
            />
            <Redirect
                from="/profile/:kaid/programs"
                to="/profile/:kaid/projects"
            />
            <Route
                path="/profile/:kaid/discussion/:tabName?"
                render={({match}) =>
                    query.loading ? (
                        <RouteLoadSpinner />
                    ) : (
                        <ProfileProvider kaid={getKaid(match)}>
                            {(profilePromise) => (
                                <Discussion
                                    kaidOrUsername={getKaid(match)}
                                    tabName={urlPathToFeedbackType(
                                        match.params["tabName"],
                                    )}
                                    profilePromise={profilePromise}
                                    profilePath={match.url.replace(
                                        /\/discussion.*/,
                                        "",
                                    )}
                                    isMyPage={isMyPage}
                                    onSufficientlyUsable={() =>
                                        sufficientlyLoaded(
                                            "lihp_profile_discussion",
                                        )
                                    }
                                />
                            )}
                        </ProfileProvider>
                    )
                }
            />
            <Redirect from="/profile/nouser" to={profileRedirect} />
            <Route
                path="/profile/:kaid"
                render={({match}) =>
                    query.loading ? (
                        <RouteLoadSpinner />
                    ) : (
                        <ProfileProvider kaid={getKaid(match)}>
                            {(profilePromise) => (
                                <Profile
                                    kaid={getKaid(match)}
                                    profilePromise={profilePromise}
                                    isMyPage={isMyPage}
                                    onSufficientlyUsable={() =>
                                        sufficientlyLoaded("lihp_profile")
                                    }
                                />
                            )}
                        </ProfileProvider>
                    )
                }
            />
            <Redirect from="/profile" to={profileRedirect} />
            <Redirect
                from="/coaches"
                to={`/profile/me/teachers${location.search}`}
            />
            {">"}
            <Route
                path="/"
                render={renderDefault(query.loading, defaultRoute, query)}
            />
        </Switch>
    );
};

type ExportProps = WithoutUserProfile<
    WithoutLocation<
        JSX.LibraryManagedAttributes<
            typeof ContentNav,
            React.ComponentProps<typeof ContentNav>
        >
    >
>;

export default compose(
    withLocation,
    withUserProfile,
)(ContentNav) as React.ComponentType<ExportProps>;
